import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { WorkflowListing } from "../../../../api/content/workflowsApi";
import ErrorNotice from "../../../../errors/ErrorNotice";
import AppButton from "../../../../ui/buttons/AppButton";
import { refusalText } from "../../../../ui/feedback/refusalWords";
import { useThreadContext } from "../threadPageContext";
import { actionOffer } from "../viewerActions";
import { useSwitchOptions } from "./useWorkflowSwitch";
import WorkflowSwitch from "./WorkflowSwitch";

interface WorkflowAttachProps {
  /** The content version's Workflows; `null` while they load. */
  listing: WorkflowListing | null;
  lastEntryNumber: number;
}

/**
 * A free Thread's way to a Workflow: the same switch as at any step (F126), from no Workflow,
 * so the Workflows offered are those its bindings fit. Only its host sees it.
 */
export default function WorkflowAttach({ listing, lastEntryNumber }: WorkflowAttachProps) {
  const { t } = useTranslation();
  const { threadId, overview } = useThreadContext();
  const [open, setOpen] = useState(false);
  const options = useSwitchOptions(threadId, open ? "open" : null);
  // The host's entry in the log, as in a saved Thread too; it runs nothing. Module C decides (W-22 slice 4).
  const offer = actionOffer(overview.viewer, "switch_workflow");
  if (offer === null) return null;
  if (!open) {
    return (
      <div className="flex justify-end">
        <AppButton variant="secondary" size="xs" unavailable={offer.refusal === null ? undefined : refusalText(t, offer.refusal)} onClick={() => { setOpen(true); }}>
          {t("threads.switch.attach")}
        </AppButton>
      </div>
    );
  }
  return (
    <section aria-label={t("threads.switch.attach")} className="app-card space-y-2 p-3">
      {options.options === null ? (
        options.error === null ? <p className="app-text-muted text-xs">{t("threads.prepare.run.loadingWorkflows")}</p> : <ErrorNotice error={options.error} compact={true} />
      ) : (
        <WorkflowSwitch options={options.options} listing={listing} lastEntryNumber={lastEntryNumber} onClose={() => { setOpen(false); }} />
      )}
    </section>
  );
}
